import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useStragglerAlerts } from '@/hooks/queries/useStragglerAlerts';
import { formatAmount, formatDate } from '../utils';
import { answered } from '@/lib/reportsRead';

/**
 * Boundary-straggler HIGHLIGHT on the close hub (ADR-071 amendment, #575).
 *
 * The straggler catch now LANDS a synced line that dates into an already-CLOSED reporting period on
 * the all-time ledger as an Unattributed, closed-period row (the amber "Needs amendment" band) —
 * inert to every filed total until classified. This card is the top-of-ledger EXPLAINER for those
 * rows: it names each straggler (which transaction, how much, which period) and deep-links into the
 * affected report, where the existing NeedsAmendment → amend affordance lives (no parallel amend
 * flow is built).
 *
 * There is NO "Mark handled" acknowledge (the #565 bespoke acknowledge was retired). An alert closes
 * server-side when its landed ledger row is classified (→ the amendment path) or excluded
 * (exclude-from-report) — the ledger's own vocabulary. The card just reflects the live open set;
 * empty → renders nothing (no card, no noise).
 */
export function BoundaryStragglerAlerts() {
  const alerts = answered(useStragglerAlerts(undefined));

  if (!alerts || alerts.length === 0) return null;

  return (
    <div
      data-testid="boundary-straggler-alerts"
      data-slot="close-straggler-alerts"
      className="rounded-lg border border-amber-200 bg-amber-50 p-4"
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-amber-900">
            {alerts.length === 1
              ? 'A synced bank transaction belongs to a closed reporting period'
              : `${alerts.length} synced bank transactions belong to closed reporting periods`}
          </p>
          <p className="mt-0.5 text-[12.5px] leading-snug text-amber-800">
            These posted after their period&apos;s filing deadline, so they&apos;re on your ledger but
            not on the filing yet. Classify or exclude each on the ledger — classifying one routes it
            into the affected report&apos;s amendment.
          </p>

          <ul className="mt-3 flex flex-col gap-2">
            {alerts.map((a) => (
              <li
                key={a.id}
                data-testid="straggler-alert"
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 rounded-md border border-amber-200 bg-white px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-slate-900">
                    {formatAmount(a.amount)}
                    <span className="text-slate-400"> · </span>
                    {formatDate(a.transactionDate)}
                    <span className="text-slate-400"> · </span>
                    <span className="text-slate-600">{a.description}</span>
                  </div>
                  <div className="mt-0.5 text-[12px] text-slate-500">
                    Belongs to the {formatDate(a.periodStart)} – {formatDate(a.periodEnd)} period
                  </div>
                </div>

                {a.affectedReportId && (
                  // Deep-link into the existing amendment affordance rather than duplicating it: the
                  // Amend button (with its jurisdiction-aware confirm dialog + useAmendReport) lives
                  // on the reports list for a NeedsAmendment report. The card names the report so the
                  // treasurer knows which row to amend.
                  <Link
                    to="/admin/finance/reports"
                    data-testid={`straggler-amend-link-${a.id}`}
                    className="shrink-0 text-[12.5px] font-medium text-amber-700 underline hover:text-amber-800"
                  >
                    Amend {a.affectedReportName}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
